import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import OnboardingWizard from './onboarding/OnboardingWizard';
import { auth } from '../utils/auth';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * IntroSlidesWrapper - Manages the intro slides modal display logic
 * Shows intro slides to new users after signup/login if they haven't seen them
 */
const IntroSlidesWrapper = ({ children }) => {
  const [showIntroSlides, setShowIntroSlides] = useState(false);
  const [userPlan, setUserPlan] = useState('SOLO');
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkOnboardingStatus();
  }, [location.pathname]);

  // Listen for custom event to trigger onboarding
  useEffect(() => {
    const handleTriggerOnboarding = () => {
      const user = auth.getUser();
      setUserPlan(user?.role === 'LAWYER' ? 'SOLO' : 'USER');
      setShowIntroSlides(true);
    };

    window.addEventListener('trigger-onboarding', handleTriggerOnboarding);
    return () => window.removeEventListener('trigger-onboarding', handleTriggerOnboarding);
  }, []);

  const checkOnboardingStatus = async () => {
    // Only check on specific routes after authentication
    const protectedRoutes = ['/lexy/dashboard', '/lexy/onboarding', '/lexy/command'];
    const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));
    
    if (!isProtectedRoute) {
      setIsChecking(false);
      return;
    }

    const token = auth.getToken();
    const user = auth.getUser();

    if (!token || !user) {
      setIsChecking(false);
      return;
    }

    try {
      // Fetch fresh user data to check onboarding status
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userData = response.data;
      
      // Check if user has seen intro slides
      const hasSeenIntroSlides = userData.onboarding_status?.has_seen_intro_slides;
      
      if (!hasSeenIntroSlides) {
        // User hasn't seen intro slides - show them
        setUserPlan(userData.role === 'LAWYER' ? 'SOLO' : 'USER');
        setShowIntroSlides(true);
      }

      // Update user in localStorage with fresh data
      auth.saveUser(userData);
      
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleComplete = () => {
    setShowIntroSlides(false);
    
    // Update user in localStorage to reflect completed intro slides
    const user = auth.getUser();
    if (user) {
      user.onboarding_status = {
        has_seen_intro_slides: true,
        intro_slides_completed_at: new Date().toISOString(),
        intro_slides_skipped: false
      };
      auth.saveUser(user);
    }

    // Stay on dashboard - no redirect needed
    // User is now ready to explore Lexy
  };

  const handleSkip = () => {
    setShowIntroSlides(false);
    
    // Update user in localStorage to reflect skipped intro slides
    const user = auth.getUser();
    if (user) {
      user.onboarding_status = {
        has_seen_intro_slides: true,
        intro_slides_completed_at: null,
        intro_slides_skipped: true
      };
      auth.saveUser(user);
    }

    // Stay on dashboard - no redirect needed
  };

  // Show loading state while checking
  if (isChecking) {
    return children;
  }

  // Get user for onboarding wizard
  const user = auth.getUser();

  return (
    <>
      {children}
      {showIntroSlides && user && (
        <OnboardingWizard
          user={user}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      )}
    </>
  );
};

export default IntroSlidesWrapper;
